import type { ApplianceType, MediaType, TagData } from 'src/helper/shared-types';

export function isSingleActivityItem(item: ActivityItem): item is SingleActivityItem {
  return item.group_type === 'single';
}

export function isGroupedActivityItem(item: ActivityItem): item is GroupedActivityItem {
  return item.group_type === 'multiple';
}

// ============================================
// GENERIC TYPE DEFINITIONS
// ============================================

export interface ActivityLogItemProps {
  createdAt: string;
  priority: 'high' | 'normal';
  timezone: string;
  activityLogId: string;
  actorFullname?: string;
  workOrderId?: string;
  workOrderNumber?: string;
  isWoDeleted?: boolean;
}

type ServiceRequestActivityType =
  | 'sr_insights'
  | 'sr_received_pms'
  | 'sr_received_lumi'
  | 'sr_created_internal'
  | 'sr_status_changed'
  | 'sr_assigned'
  | 'sr_assignee_removed'
  | 'sr_priority_changed'
  | 'sr_resident_changed'
  | 'sr_description_changed'
  | 'sr_media_updated'
  | 'sr_auto_closed';

// ⚙️ Work Orders
type WorkOrderActivityType =
  | 'wo_created'
  | 'wo_deleted'
  | 'wo_status_changed'
  | 'wo_priority_changed'
  | 'wo_nte_changed'
  | 'wo_tag_added'
  | 'wo_due_date_changed'
  | 'wo_due_date_removed'
  | 'wo_assigned'
  | 'wo_assignee_removed'
  | 'wo_health_at_risk'
  | 'wo_health_critical'
  | 'wo_qc_auto_passed';

// 🪲 Issues
type IssueActivityType =
  | 'issue_added_manual'
  | 'issue_generated_ai'
  | 'issue_edited'
  | 'issue_deleted'
  | 'issue_restored'
  | 'issue_canceled'
  | 'issue_assigned_to_wo'
  | 'issue_reassigned_to_wo'
  | 'issue_unassigned_from_wo'
  | 'issue_qc_passed'
  | 'issue_qc_unresolved'
  | 'issue_qc_needs_info'
  | 'issue_qc_info_submitted';

type ApplianceIssueActivityType = 'appliance_added';

// 💬 Messages
type MessageActivityType = 'message_received' | 'message_sent' | 'outbound_sms';

// 🧾 Quotes
type QuoteActivityType =
  | 'quote_submitted'
  | 'quote_created'
  | 'quote_updated'
  | 'quote_deleted'
  | 'quote_sent_for_approval'
  | 'quote_approved'
  | 'quote_restored'
  | 'quote_rejected'
  | 'lula_quote_approved'
  | 'lula_quote_rejected'
  | 'lula_quote_submitted'
  | 'lula_quote_restored';

// 🧾 Invoices
type InvoiceActivityType = 'invoice_created' | 'invoice_received_lula';

// 🚚 Trips
type TripActivityType =
  | 'trip_scheduled'
  | 'trip_rescheduled'
  | 'trip_canceled'
  | 'trip_closed'
  | 'trip_status_changed'
  | 'wo_dispatched'
  | 'wo_vendor_claimed'
  | 'wo_vendor_passed'
  | 'wo_sent_to_lula'
  | 'wo_lula_assigned';

// 🕓 Availability
type AvailabilityActivityType =
  | 'availability_request_sent'
  | 'availability_received'
  | 'availability_added_internal'
  | 'additional_availability_received'
  | 'additional_availability_approved'
  | 'additional_availability_declined';

// 📝 Notes
type InternalNoteActivityType = 'note_added' | 'note_deleted';

// Grouped
type GroupedActivityType = 'grouped';

// ✅ Final combined type
export type ActivityType =
  | ServiceRequestActivityType
  | WorkOrderActivityType
  | IssueActivityType
  | MessageActivityType
  | QuoteActivityType
  | InvoiceActivityType
  | TripActivityType
  | AvailabilityActivityType
  | InternalNoteActivityType
  | GroupedActivityType
  | ApplianceIssueActivityType;

export interface BaseActivityItem {
  activity_log_id: string;
  type: ActivityType;
  created_at: string;
  priority: 'high' | 'normal';
  group_type: 'single' | 'multiple';
  is_wo_deleted?: boolean;
}

type Obj = {
  label: string;
  value: string;
};

type IssueCategory = {
  id: string;
  label: string;
};

export type IssueData = {
  title: string;
  category?: IssueCategory;
  sub_category?: IssueCategory;
  problem_diagnosis?: IssueCategory;
};

export type AiInsight = {
  slug: string;
  description: string;
  title: string;
  isPropertyLevel?: boolean;
};
export type QuoteData = {
  total: {
    max_in_cents: number;
    min_in_cents: number;
  };
  approved_amount: number;
  quote_id: string;
  show_review_quote_button: boolean;
  quote_number: string;
  // lula schedule wo quote specific fields
  total_cost_in_cents: number;
  public_url: string;
};

export type IssueCount = {
  done_count: number;
  total_issues: number;
  quoted_count: number;
  unresolved_count: number;
  missing_count: number;
  canceled_count: number;
};

type ScheduleDateTime = {
  scheduled_start_time: string;
  scheduled_end_time: string;
};
export type AvailabilityDate = {
  date: string;
  timings: Array<string>;
};

export type OutboundNotificationActivity = {
  label: string;
  renderedBody: string | null;
  recipientRole: string;
  status: string;
  attemptCount: number;
  quietHoursHeld: boolean;
  scheduledFor?: string | null;
  recipientName?: string | null;
};

/**
 * One issue's vendor-reported outcome, as recorded on an automatic quality-check
 * pass. Carried on the attributes but not rendered today — the timeline card
 * shows counts, following `TripClosed`. Exported so a per-issue list can be
 * built later without reopening this file.
 */
export type QualityCheckAutoPassOutcome = {
  work_order_issue_id: string;
  title: string | null;
  vendor_outcome: string;
  vendor_outcome_label: string;
};

export type LogAttributes = {
  // Actor information
  actor?: {
    fullname?: string;
    role?: string;
    actor_id?: string;
  };
  // Assignment
  assignee?: {
    fullname?: string;
    role?: string;
  };

  // Priority changes
  from_priority?: Obj;
  to_priority?: Obj;

  // Status
  to_status?: Obj;

  // Service request attributes
  sr_source?: string;
  pms_name?: string;
  pms_id?: string;
  coordinator?: {
    fullname: string;
  };

  // Issue
  issue_title?: string;
  issue_titles?: string[];
  issue_id?: string;
  can_undo?: boolean;
  can_restore?: boolean;
  report_summary?: IssueCount;

  // AI insights
  insights?: AiInsight[];

  // Notes
  note_id?: string;
  note?: string;
  is_editable?: boolean;

  // Quote attributes
  line_item_count?: number;
  quote?: QuoteData;
  approver?: {
    fullname: string;
    role: string;
  };
  rejected_user?: {
    fullname: string;
    role: string;
  };
  cancel_reason?: string;
  rejection_reason?: string;

  // Trip attributes
  trip?: {
    trip_number: string;
    show_qc_button: boolean;
  };
  provider?: {
    fullname: string;
    type: string;
  };
  schedule_date_time?: ScheduleDateTime;
  old_schedule_date_time?: ScheduleDateTime;
  vendor_count?: number;
  vendor_name?: string;

  // Resident
  from_resident?: {
    fullname: string;
  };
  to_resident?: {
    fullname: string;
  };
  resident?: {
    fullname: string;
  };
  message?: string;
  availability_url?: string;
  availability_list?: AvailabilityDate[];
  additional_availability_list?: Array<{
    date: string;
    timings: Array<{
      availability_request_id: string;
      timing: string;
    }>;
  }>;
  show_approve_btn?: boolean;
  show_decline_btn?: boolean;

  organization_name?: string;
  owner?: {
    fullname: string;
  };

  // Tags
  tags?: TagData[];

  // Nte
  nte_old?: string;
  nte_new?: string;

  // due date
  old_due_date?: string;
  new_due_date?: string;

  // work order
  work_order?: {
    work_order_number?: string;
    work_order_id?: string;
  };
  new_work_order?: {
    work_order_number?: string;
    work_order_id?: string;
  };

  // Vendor additional availability
  requested_user?: {
    user_id?: string;
    fullname?: string;
    user_type?: string;
  };
  vendor?: {
    fullname?: string;
    name?: string;
    vendor_id: string;
  };
  vendor_availability_id?: string;
  selected_availability?: {
    date: string;
    timing: string;
    start_time: string;
    end_time: string;
  };
  availability_request?: {
    vendor: {
      name: string;
      vendor_id: string;
    };
  };
  approved_availability?: {
    date: string;
    timings: string;
    availability_request_id: string;
  };
  can_approve?: boolean;
  can_decline?: boolean;
  // Invoice attributes
  invoice?: {
    total_cost_in_cents?: number;
    invoice_number?: string;
    invoice_id?: string;
    pdf_link?: string;
  };
  // Appliance
  appliance?: ApplianceType;
  issue?: IssueData;

  // Lumi service request attributes
  description?: string;
  property_info?: {
    property_access_method?: {
      label?: string;
      value?: string;
    };
  };
  pets?: Array<string>;
  other_pets?: string;
  additional_notes?: string;
  media?: MediaType[];
  // Chat message attributes
  chat_message?: {
    content?: string;
    attachments?: MediaType[];
  };
  outbound_notification?: {
    label: string;
    rendered_body: string | null;
    recipient_role: string;
    status: string;
    attempt_count: number;
    quiet_hours_held: boolean;
    scheduled_for?: string | null;
    recipient_name?: string | null;
  };
  // Quality check auto-pass (wo_qc_auto_passed). Flat keys because the backend
  // sends them flat. `summary` is the backend's own sentence; it is typed here
  // because the backend sends it, but the card deliberately does NOT render it —
  // it restates the card's own headline. See QualityCheckAutoPassed.tsx.
  trigger?: string;
  config_key?: string;
  outcome?: 'passed' | 'sent_back';
  summary?: string;
  /**
   * Sparse, keyed by raw trip-issue status (`done`, `unresolved`,
   * `de_escalated`) — only statuses that actually occurred appear. PHP
   * serialises the empty case as `[]` rather than `{}`, so read this with
   * `Object.entries`, never by indexing a known key.
   */
  vendor_outcome_counts?: Record<string, number>;
  vendor_issue_outcomes?: QualityCheckAutoPassOutcome[];
};

/**
 * Single activity item
 */
export interface SingleActivityItem extends BaseActivityItem {
  group_type: 'single';
  attributes: LogAttributes;
}

/**
 * Grouped activity item (collapsed by default)
 */
export interface GroupedActivityItem extends BaseActivityItem {
  group_type: 'multiple';
  attributes: {
    entries?: string[]; // Array of activity_log_ids
  };
}

/**
 * Union type for all activity items
 */
export type ActivityItem = SingleActivityItem | GroupedActivityItem;

export type ActivityLogEntityType = 'service-request' | 'work-order';

export interface ActivityLogSummaryData {
  service_request_id: string;
  address: string;
  job_summary: string;
  current_status: {
    label: string;
    value: string;
  };
  status_details: string[];
  last_updated_by: string;
  last_updated_date: string;
  is_ai_summary_outdated?: boolean;
  timeline: {
    work_order_id?: string;
    timestamp_utc: string;
    work_order_number?: string;
    event_type: string;
    description: string;
  }[];
  notes: {
    delays: string[];
    outstanding_items: string[];
  };
  latest_work_order_summaries: {
    work_order_id: string;
    summary: string;
  }[];
}

export interface ActivityLogState {
  currentSrOrWoId: string;

  currentPage: 'work-orders' | 'service-requests' | '';

  // Normalized entities: key = activity_log_id, value = activity object
  source: Record<string, ActivityItem>;

  // Ordered array of activity_log_ids (maintains display order)
  logIds: string[];

  meta: {
    next: string;
    prev: string;
    per_page: number;
  };

  // Loading state for initial fetch
  loading: boolean;

  // Track which groups are currently being expanded
  expandingGroups: Record<string, boolean>;

  // persist actively typing or typed message that are not sent
  // until the offcanvas is closed
  draftedInternalNote: {
    note: string;
    logId?: string;
    noteId?: string;
  };

  /**
   * Holds original notes before optimistic updates before editing
   */
  originalNote: string;

  // AI Summary data for the SR or WO
  summary: Partial<ActivityLogSummaryData>;
  isFetchingSummary: boolean;
  hasSummaryApiFailed: boolean;
}

// ============================================
// INITIAL STATE
// ============================================

export const initialState: ActivityLogState = {
  currentSrOrWoId: '',
  currentPage: '',
  source: {},
  logIds: [],
  meta: {
    next: '',
    prev: '',
    per_page: 0,
  },
  loading: false,
  expandingGroups: {},
  draftedInternalNote: {
    note: '',
  },
  originalNote: '',
  summary: {},
  isFetchingSummary: false,
  hasSummaryApiFailed: false,
};
